import { Filter, OrderBy } from '@shared/types';

const TABS_STORAGE_KEY = 'firestore-explorer-tabs';
const QUERIES_STORAGE_KEY = 'firestore-explorer-queries';

export interface StoredTabsByConnection {
  [connectionId: string]: { tabIds: string[] };
}

export interface PersistedQueryState {
  conditions: Filter[];
  orConditions: Filter[];
  orderBy: OrderBy[];
  limit: number;
  documentIdPrefix?: string;
  listOrderBy?: OrderBy | null;
}

export interface StoredQueriesByConnection {
  [connectionId: string]: { [collectionId: string]: PersistedQueryState };
}

export function loadTabIds(connectionId: string): string[] {
  if (!connectionId) return [];
  try {
    const stored = localStorage.getItem(TABS_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as StoredTabsByConnection;
    return parsed[connectionId]?.tabIds ?? [];
  } catch {
    return [];
  }
}

export function saveTabIds(connectionId: string, tabIds: string[]): void {
  if (!connectionId) return;
  try {
    const stored = localStorage.getItem(TABS_STORAGE_KEY);
    const parsed: StoredTabsByConnection = stored ? JSON.parse(stored) : {};
    parsed[connectionId] = { tabIds };
    localStorage.setItem(TABS_STORAGE_KEY, JSON.stringify(parsed));
  } catch (e) {
    console.error('Failed to save tab ids', e);
  }
}

export function loadQueryState(
  connectionId: string,
  collectionId: string
): PersistedQueryState | null {
  if (!connectionId || !collectionId) return null;
  try {
    const stored = localStorage.getItem(QUERIES_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as StoredQueriesByConnection;
    return parsed[connectionId]?.[collectionId] ?? null;
  } catch {
    return null;
  }
}

export function loadAllQueryStatesForConnection(
  connectionId: string
): Record<string, PersistedQueryState> {
  if (!connectionId) return {};
  try {
    const stored = localStorage.getItem(QUERIES_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored) as StoredQueriesByConnection;
    return parsed[connectionId] ?? {};
  } catch {
    return {};
  }
}

export function saveQueryState(
  connectionId: string,
  collectionId: string,
  state: PersistedQueryState
): void {
  if (!connectionId || !collectionId) return;
  try {
    const stored = localStorage.getItem(QUERIES_STORAGE_KEY);
    const parsed: StoredQueriesByConnection = stored ? JSON.parse(stored) : {};
    if (!parsed[connectionId]) parsed[connectionId] = {};
    parsed[connectionId][collectionId] = state;
    localStorage.setItem(QUERIES_STORAGE_KEY, JSON.stringify(parsed));
  } catch (e) {
    console.error('Failed to save query state', e);
  }
}

export function clearQueryState(connectionId: string, collectionId: string): void {
  if (!connectionId || !collectionId) return;
  try {
    const stored = localStorage.getItem(QUERIES_STORAGE_KEY);
    if (!stored) return;
    const parsed = JSON.parse(stored) as StoredQueriesByConnection;
    if (!parsed[connectionId]?.[collectionId]) return;
    delete parsed[connectionId][collectionId];
    if (Object.keys(parsed[connectionId]).length === 0) delete parsed[connectionId];
    localStorage.setItem(QUERIES_STORAGE_KEY, JSON.stringify(parsed));
  } catch (e) {
    console.error('Failed to clear query state', e);
  }
}

export function clearExplorerTabsStorage(): void {
  try {
    localStorage.removeItem(TABS_STORAGE_KEY);
    localStorage.removeItem(QUERIES_STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear explorer tabs storage', e);
  }
}
